import { useState, useRef, useEffect } from 'react';
import type { RiskAssessmentConfig, LevelScaleItem } from '@safetyhub/shared';
import { normalizedToLevel, getThreeLevelScale } from '@safetyhub/shared';
import { hexToTailwindStyle, hexToInlineStyle } from '../../../lib/risk-color-utils';

interface EditableRiskBadgeProps {
  type: 'likelihood' | 'severity' | 'riskLevel';
  value: number;
  label: string;
  config?: RiskAssessmentConfig | null;
  onSave: (value: number) => Promise<void>;
  disabled?: boolean;
}

// level(1~N)을 1~10 score 범위 중간값으로 변환
// 3단계: 1→2, 2→5, 3→8  /  5단계: 1→1, 2→3, 3→5, 4→7, 5→9
function levelToScore(level: number, levelCount: number): number {
  return Math.round((level - 0.5) * 10 / levelCount);
}

interface DropdownOption {
  score: number;
  label: string;
  level: number;
}

function buildOptions(
  config: RiskAssessmentConfig | null | undefined,
  type: 'likelihood' | 'severity',
): DropdownOption[] | null {
  const criteria = type === 'likelihood'
    ? config?.likelihoodCriteria
    : config?.severityCriteria;

  if (!criteria || criteria.length === 0) return null;

  return criteria.map((c) => ({
    score: levelToScore(c.level, criteria.length),
    label: c.label,
    level: c.level,
  }));
}

/**
 * 이 배지가 재는 축의 등급 척도.
 *
 * riskLevel 축은 THREE_LEVEL 등급 척도이고, 그 해소는 shared 단일 진입점을 거친다 —
 * 정본은 경계표(`riskLevelCriteria`)이고 `config.levelCriteria`는 옛 데이터 폴백일 뿐이라,
 * 직접 읽으면 경계표만 있는 신규 세트에서 빈 배열을 받아 **위험도 선택 옵션 자체가 사라진다.**
 * 빈도/강도 축은 각자의 기준 배열을 그대로 쓴다.
 */
function criteriaFor(
  config: RiskAssessmentConfig | null | undefined,
  type: 'likelihood' | 'severity' | 'riskLevel',
): LevelScaleItem[] {
  if (!config) return [];
  if (type === 'riskLevel') return getThreeLevelScale(config);
  return type === 'likelihood' ? config.likelihoodCriteria : config.severityCriteria;
}

// THREE_LEVEL 등급 척도 → riskLevel 옵션 빌드
// level(1~N)을 1~10 score 중간값으로 변환하여 반환
function buildRiskLevelOptions(
  config: RiskAssessmentConfig | null | undefined,
): DropdownOption[] | null {
  if (config?.methodType !== 'THREE_LEVEL') return null;
  // getThreeLevelScale이 이미 min 오름차순 순번으로 level을 부여해 돌려주므로 추가 정렬이 필요 없다.
  const criteria = criteriaFor(config, 'riskLevel');
  if (criteria.length === 0) return null;

  return criteria.map((c) => ({
    score: levelToScore(c.level, criteria.length),
    label: c.label,
    level: c.level,
  }));
}

/** hex color → Tailwind hover 포함 배지 클래스 (공통 헬퍼 + hover 접미) */
function hexToTailwindStyleWithHover(color: string | undefined): string | null {
  const base = hexToTailwindStyle(color);
  if (!base) return null;
  // base: "bg-red-100 text-red-700 border-red-200" → hover 추가
  const hoverMap: Record<string, string> = {
    'bg-red-100 text-red-700 border-red-200': 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200',
    'bg-orange-100 text-orange-700 border-orange-200': 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200',
    'bg-yellow-100 text-yellow-700 border-yellow-200': 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200',
    'bg-lime-100 text-lime-700 border-lime-200': 'bg-lime-100 text-lime-700 border-lime-200 hover:bg-lime-200',
    'bg-green-100 text-green-700 border-green-200': 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200',
    'bg-emerald-100 text-emerald-700 border-emerald-200': 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200',
    'bg-blue-100 text-blue-700 border-blue-200': 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
    'bg-gray-100 text-gray-700 border-gray-200': 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
  };
  return hoverMap[base] ?? `${base} hover:brightness-95`;
}

// config의 criteria level → color → Tailwind 색상 결정
// config에 color 있으면 우선 사용, 없으면 level 비율 fallback
// 반환: { className, style? } — 미매칭 hex는 inline style로 처리
function getLevelStyle(
  value: number,
  config: RiskAssessmentConfig | null | undefined,
  type: 'likelihood' | 'severity' | 'riskLevel',
): { className: string; style?: React.CSSProperties } {
  const criteria = criteriaFor(config, type);

  if (criteria.length > 0) {
    const criterion = normalizedToLevel(value, criteria);
    if (criterion) {
      const hex = (criterion as { color?: string }).color;
      // config color 우선 (THREE_LEVEL의 상/중/하 각 색상 그대로 사용)
      const colorStyle = hexToTailwindStyleWithHover(hex);
      if (colorStyle) return { className: colorStyle };

      // 사전 정의 미매칭 hex: inline style fallback
      if (hex && /^#[0-9a-f]{6}$/i.test(hex)) {
        const inlineStyle = hexToInlineStyle(hex);
        if (inlineStyle) return { className: 'border hover:brightness-95', style: inlineStyle };
      }

      // color 없는 경우 level 비율 fallback
      const ratio = criterion.level / criteria.length;
      if (ratio >= 0.85) return { className: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' };
      if (ratio >= 0.55) return { className: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200' };
      if (ratio >= 0.3) return { className: 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200' };
      return { className: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' };
    }
  }

  // config 없으면 중립 스타일
  return { className: 'bg-ink-100 text-ink-700 border-ink-200 hover:bg-ink-200' };
}

export function EditableRiskBadge({
  type,
  value,
  label,
  config,
  onSave,
  disabled = false,
}: EditableRiskBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const typeLabel = type === 'likelihood' ? '빈도' : type === 'severity' ? '강도' : '위험도';
  const options = type === 'riskLevel'
    ? buildRiskLevelOptions(config)
    : buildOptions(config, type);
  // config 없으면 편집 불가
  const isEditable = !disabled && options !== null;
  const badgeStyle = getLevelStyle(value, config, type);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = async (score: number, optLevel: number) => {
    if (isSaving) return;
    // level 단위로 동일성 체크: 현재 value가 이미 같은 level이면 skip
    // (DB값 6과 opt score 5는 둘 다 level=2이므로 같은 선택으로 취급)
    const criteria = criteriaFor(config, type);
    const currentLevel = criteria.length > 0
      ? normalizedToLevel(value, criteria)?.level
      : undefined;
    if (currentLevel !== undefined && optLevel === currentLevel) return;
    if (currentLevel === undefined && score === value) return;

    setIsSaving(true);
    try {
      await onSave(score);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to save risk value:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditable) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${badgeStyle.className}`}
        style={badgeStyle.style}
      >
        <span className="opacity-70">{typeLabel}:</span>
        <span className="font-medium">{label}</span>
      </span>
    );
  }

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSaving}
        className={`
          inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs
          cursor-pointer transition-colors
          ${badgeStyle.className}
          ${isSaving ? 'opacity-50' : ''}
        `}
        style={badgeStyle.style}
        title={`${typeLabel} 변경 (클릭)`}
      >
        <span className="opacity-70">{typeLabel}:</span>
        <span className="font-medium">{label}</span>
        <svg className="w-3 h-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-card shadow-card border border-line py-1 min-w-[140px]">
          <div className="px-2 py-1 text-xs text-ink-500 border-b border-line mb-1">
            {typeLabel} 선택
          </div>
          {(() => {
            const criteria = criteriaFor(config, type);
            const currentLevel = criteria.length > 0
              ? normalizedToLevel(value, criteria)?.level
              : undefined;
            return options.map((opt) => (
              <button
                key={opt.score}
                type="button"
                onClick={() => handleSelect(opt.score, opt.level)}
                className={`
                  w-full px-3 py-1.5 text-left text-xs
                  ${opt.level === currentLevel
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : 'hover:bg-surface-hover text-ink-700'
                  }
                `}
              >
                {opt.label}
              </button>
            ));
          })()}
        </div>
      )}
    </div>
  );
}

export default EditableRiskBadge;
